/**
 * シフト変更のお知らせ(本人向け。2026-09-22 D-130)。
 * 管理者が提出済みのシフトの時間を直すと出る。「確認しました」を押すと消える(同じ日は1件にまとまる)。
 * 本人成績(ログイン直後の画面)とシフトの上に出す。管理者・閲覧者には出さない。
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import AlertPanel, { type AlertItem } from '@core/components/ui/AlertPanel';
import { weekdayLabel } from '@core/lib/date';
import type { DateStr } from '@core/lib/types';
import { acknowledgeNotices, fetchMyNotices, slotLabel, type ShiftNotice } from './shiftNoticeRepo';

const fmtH = (h: number) => `${Math.round(h * 100) / 100}時間`;

export default function ShiftNoticePanel({ showLink = false }: { showLink?: boolean }) {
  const { user, isOperator } = useAuth();
  const toast = useToast();
  const opId = isOperator ? (user?.op_id ?? '') : '';
  const [notices, setNotices] = useState<ShiftNotice[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!opId) {
      setNotices([]);
      return;
    }
    void fetchMyNotices(opId)
      .then((n) => alive && setNotices(n))
      .catch(() => alive && setNotices([]));
    return () => {
      alive = false;
    };
  }, [opId]);

  if (!opId || !user || notices.length === 0) return null;

  const items: AlertItem[] = notices.flatMap((n) =>
    Object.values(n.items)
      .sort((a, b) => a.day.localeCompare(b.day))
      .map((it): AlertItem => {
        const date = `${n.month}-${it.day}` as DateStr;
        // 出勤形態が変わった日だけ「出勤」「在宅」を添える
        const withStyle = (it.before?.style ?? '') !== (it.after?.style ?? '');
        return {
          id: `${n.month}-${it.day}`,
          severity: 'warning',
          target: `${Number(n.month.slice(5))}月${Number(it.day)}日${weekdayLabel(date)}`,
          message: (
            <>
              {slotLabel(it.before, withStyle)} → <b>{slotLabel(it.after, withStyle)}</b>
            </>
          ),
          diff: `${fmtH(it.before_hours)} → ${fmtH(it.after_hours)} ・ ${it.by} が変更`,
        };
      }),
  );

  const ack = async () => {
    setBusy(true);
    try {
      await acknowledgeNotices(notices, user.id);
      setNotices([]);
      toast('確認しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertPanel
      title="シフトが変更されました"
      meta="提出したシフトを管理者が直しました"
      items={items}
      actions={
        <>
          {showLink && (
            <Link className="ui-btn ui-btn-secondary ui-btn-sm" to="/shift?tab=submit">
              シフトを見る
            </Link>
          )}
          <button type="button" className="ui-btn ui-btn-primary ui-btn-sm" disabled={busy} onClick={() => void ack()}>
            {busy ? '保存中…' : '確認しました'}
          </button>
        </>
      }
    />
  );
}
